import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

/** Returns `true` when the save succeeded and it is safe to leave. */
type SaveHandler = () => Promise<boolean> | boolean;

interface UnsavedChangesApi {
  /** A form below this provider currently holds edits that were never saved. */
  isDirty: boolean;
  /**
   * Publish the current form's dirty state (and how to save it) to the guard.
   * Call from an effect; call with `false` on unmount.
   */
  setUnsavedChanges: (dirty: boolean, save?: SaveHandler) => void;
  /**
   * Run `action`, but if there are unsaved changes ask the admin first whether
   * to save, discard, or stay. `action` runs unchanged when nothing is dirty,
   * so callers can wrap navigation handlers unconditionally.
   */
  guard: (action: () => void) => void;
}

const noop = () => undefined;

const UnsavedChangesContext = createContext<UnsavedChangesApi>({
  isDirty: false,
  setUnsavedChanges: noop,
  guard: (action: () => void) => action(),
});

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

/**
 * Guards navigation away from a form with unsaved edits.
 *
 * The user detail view has several exits that do not go through the router —
 * the sub-tab strip, the close button on the overview card — so a router-level
 * blocker would not see them. Instead every exit funnels through `guard()`,
 * and the browser-level exits (reload, tab close) are covered by
 * `beforeunload`.
 */
export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [saving, setSaving] = useState(false);

  // Kept in refs so `guard` and `setUnsavedChanges` stay referentially stable —
  // consumers call `setUnsavedChanges` from an effect, and an unstable identity
  // there would re-run the effect on every render.
  const isDirtyRef = useRef(false);
  const saveRef = useRef<SaveHandler | undefined>(undefined);

  const setUnsavedChanges = useCallback((dirty: boolean, save?: SaveHandler) => {
    isDirtyRef.current = dirty;
    saveRef.current = save;
    setIsDirty(dirty);
  }, []);

  const guard = useCallback((action: () => void) => {
    if (!isDirtyRef.current) {
      action();
      return;
    }
    // `useState` treats a bare function as an updater, hence the wrapper.
    setPendingAction(() => action);
  }, []);

  const runPending = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    isDirtyRef.current = false;
    setIsDirty(false);
    action?.();
  }, [pendingAction]);

  const handleSaveAndLeave = async () => {
    setSaving(true);
    try {
      const saved = await saveRef.current?.();
      // A failed save (validation, server error) must not swallow the edits —
      // stay on the form so the admin can see the reported problem.
      if (saved === false) {
        setPendingAction(null);
        return;
      }
      runPending();
    } finally {
      setSaving(false);
    }
  };

  // Reload / tab close / external link. The browser shows its own generic
  // wording here; the string is only required to trigger the prompt at all.
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const value = useMemo<UnsavedChangesApi>(
    () => ({ isDirty, setUnsavedChanges, guard }),
    [isDirty, setUnsavedChanges, guard]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <Dialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Es gibt Änderungen, die noch nicht gespeichert wurden. Sollen sie gespeichert
            werden, bevor du diese Ansicht verlässt?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} variant="text" disabled={saving}>
            Hier bleiben
          </Button>
          <Button onClick={runPending} variant="outlined" color="secondary" disabled={saving}>
            Verwerfen
          </Button>
          <Button onClick={handleSaveAndLeave} variant="contained" disabled={saving} autoFocus>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
};
